/**
 * RootLayout — wrapper compartido por todas las rutas.
 *
 * Layout raíz fijo (h-screen + overflow-hidden):
 *
 *   ┌──────────┬────────────────────────────────────┐
 *   │          │ Topbar (h-14, shrink-0, no scroll) │
 *   │ Sidebar  ├────────────────────────────────────┤
 *   │ (Logo +  │                                    │
 *   │  nav     │ <main> (flex-1, overflow-hidden)   │
 *   │  scroll  │   ↳ <Outlet />                     │
 *   │  interno │   Cada ruta es responsable de su   │
 *   │  + KD    │   propio scroll interno.           │
 *   │  abajo)  ├────────────────────────────────────┤
 *   │          │ SystemFooter (shrink-0, alineado   │
 *   │          │  horizontalmente con el tenant     │
 *   │          │  switcher del sidebar)             │
 *   └──────────┴────────────────────────────────────┘
 *
 * `<main>` NO scrollea ni tiene padding propio. Cada ruta hija decide:
 *  - rutas simples (HomeView, TemplatesView, ...): wrap en
 *    `<div className="h-full overflow-y-auto px-8 py-10">`.
 *  - rutas complejas (ProjectShell): flex column propia con header fijo,
 *    cuerpo scrolleable interno y, opcional, action bar fijo abajo.
 *
 * Esta arquitectura garantiza que sidebar, topbar, footer y demás
 * elementos fijos no se muevan jamás — solo el área de trabajo se
 * mueve por dentro.
 *
 * El estado del usuario (`me`) se carga aquí una sola vez vía useMe()
 * y se inyecta en Sidebar/Topbar.
 *
 * Una ruta puede pedir el layout enfocado (sin sidebar ni topbar) con
 * `staticData: { layout: 'focused' }` — p. ej. `/connect-agent`, donde el
 * usuario llega desde un agente de IA a decidir una sola cosa.
 */

import { Outlet, useMatches } from '@tanstack/react-router'
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react'

import { isMsalConfigured } from '../auth/msalConfig'
import { useMe } from '../auth/useMe'
import { OrganizationNotice, SuspendedView } from '../components/shell/OrganizationNotice'
import { Sidebar } from '../components/shell/Sidebar'
import { SystemFooter } from '../components/shell/SystemFooter'
import { Topbar } from '../components/shell/Topbar'
import { Toaster } from '../components/Toaster'

export function RootLayout() {
  // Cuando MSAL aún no está configurado (Fase A pre-Entra), MsalProvider
  // es passthrough y los <AuthenticatedTemplate>/<UnauthenticatedTemplate>
  // no rendean nada. Caemos al shell pre-auth para que HomeView muestre
  // el aviso de "configura VITE_ENTRA_*".
  if (!isMsalConfigured) {
    return (
      <>
        <FocusedShell />
        <Toaster />
      </>
    )
  }
  return (
    <>
      <UnauthenticatedTemplate>
        <FocusedShell />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <AuthenticatedLayout />
      </AuthenticatedTemplate>
      <Toaster />
    </>
  )
}

/** El shell que pide la ruta activa: enfocado si alguna de sus rutas lo declara. */
function AuthenticatedLayout() {
  const focused = useMatches({
    select: (matches) => matches.some((match) => match.staticData.layout === 'focused'),
  })
  return focused ? <FocusedShell /> : <AuthedShell />
}

/**
 * Shell mínimo: contenido a pantalla completa, sin sidebar ni topbar. Lo usan
 * los usuarios sin sesión (login u onboarding) y las rutas con layout
 * enfocado. Mantenemos el SystemFooter visible para que el copyright +
 * ownership se vea también aquí.
 */
function FocusedShell() {
  return (
    <div className="h-screen flex flex-col bg-canvas text-ink overflow-hidden">
      <main className="flex-1 min-h-0 overflow-y-auto">
        <Outlet />
      </main>
      <SystemFooter />
    </div>
  )
}

/**
 * Shell para usuarios autenticados: sidebar + topbar + outlet con scroll
 * + system footer. Mientras `useMe()` carga, mostramos el shell con
 * datos vacíos — Sidebar/Topbar tienen fallbacks razonables para
 * `me === undefined`.
 *
 * Layout: dos columnas (sidebar + content) que llenan el viewport.
 * El SystemFooter vive al fondo de la columna derecha — así queda
 * horizontalmente alineado con el TenantSwitcher (que está al fondo
 * del sidebar), formando una "fila inferior" visual sin spans cruzados.
 */
function AuthedShell() {
  const { me } = useMe()

  // OT-0073 §4.4 — organización suspendida: ninguna ruta del tenant se monta.
  if (me?.tenant?.access === 'suspended') {
    return (
      <div className="h-screen flex flex-col bg-canvas text-ink overflow-hidden">
        <main className="flex-1 min-h-0 overflow-y-auto grid place-items-center p-6">
          <SuspendedView
            organizationName={me.tenant.display_name}
            supportEmail={me.tenant.support_email}
          />
        </main>
        <SystemFooter />
      </div>
    )
  }

  return (
    // h-screen + overflow-hidden = layout cap a la altura del viewport.
    // Sin esto, contenido alto del Outlet hace crecer el flex container
    // y el sidebar/topbar dejan de ser "fijos" en el sentido visual.
    <div className="h-screen flex bg-canvas text-ink overflow-hidden">
      <Sidebar me={me} />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <Topbar me={me} />
        {/* Solo lectura o beta por vencer (OT-0073 §4.4): una fila más del
            flex, que no ocupa nada cuando no aplica. */}
        <OrganizationNotice me={me} />
        {/* main NO scrollea ni padea — cada ruta gestiona lo suyo.
            `min-h-0` evita que flex-1 herede min-content y se sobrepase. */}
        <main className="flex-1 min-h-0 overflow-hidden">
          <Outlet />
        </main>
        <SystemFooter />
      </div>
    </div>
  )
}
